import { rtLogService, MonitorEventSeverity, MonitorEventSource } from '@rammetec/core';
import { generateWriteBatchId, repositoryResolver, } from '@rammetec/data-access';
import { AUDIT_FIELD, AUDIT_OPERATION, } from '../../types/audit.js';
import { auditContext } from '../auditContext.js';
import { auditAggregateLabel } from './auditAggregateLabel.js';
const unavailableOrgs = new Set();
function toValueJson(value) {
    if (value === undefined)
        return null;
    return JSON.stringify(value, (_key, v) => v instanceof Date ? v.toISOString() : v);
}
/**
 * Collects field-audit entries for one save and enqueues them on a {@link WriteBatch}.
 *
 * **Failure mode (resolve):** if the audit repositories cannot be resolved (org without a PG
 * destination, etc.), logs once per org (`type: 'field-audit-unavailable'`) and
 * returns without enqueueing — the business save must still succeed.
 *
 * **Failure mode (commit on non-atomic path):** when mutation and log do not share a
 * `destinationKey`, {@link OrgScopedCrudService} commits them separately and swallows
 * log-write errors (`type: 'field-audit-write-failed'`). An atomic same-destination
 * batch still fails together — correct when both sides share one transaction.
 */
export class AuditRecorder {
    constructor(orgId, ctx = {}) {
        this.orgId = orgId;
        this.ctx = ctx;
        this.pending = [];
        // generateWriteBatchId — not crypto.randomUUID (Hermes / React Native has no randomUUID).
        this.groupId = generateWriteBatchId();
    }
    get hasPending() {
        return this.pending.length > 0;
    }
    /** First staged op — for error payloads after a split commit. */
    get firstPendingMeta() {
        const first = this.pending[0];
        return first ? { entity: first.entity, recordId: first.recordId } : null;
    }
    /**
     * Stage one operation. Update/create with an empty `changes` array is a no-op
     * (including the empty parent bump inside `withAggregateVersion`).
     */
    record(input) {
        if (input.op !== 'delete' && input.changes.length === 0)
            return;
        this.pending.push({
            entity: input.entity,
            recordId: input.recordId,
            op: input.op,
            changes: input.changes,
            snapshot: input.snapshot,
            aggregate: input.aggregate ?? {
                entity: input.entity,
                id: input.recordId,
                version: null,
                record: { id: input.recordId },
            },
        });
    }
    /**
     * Shared `destinationKey` of both audit repositories when they can join an atomic
     * batch; `null` when resolve failed or either repo lacks WriteBatchSupport / keys differ.
     *
     * Used by the local-batch path to decide atomic vs split commit.
     */
    async resolveDestinationKey() {
        const repos = await this.resolveRepos();
        if (!repos)
            return null;
        const opSupport = repos.opRepo.getWriteBatchSupport?.() ?? null;
        const fieldSupport = repos.fieldRepo.getWriteBatchSupport?.() ?? null;
        if (!opSupport || !fieldSupport)
            return null;
        if (opSupport.destinationKey !== fieldSupport.destinationKey)
            return null;
        return opSupport.destinationKey;
    }
    /**
     * Enqueue {@link AuditOperation} + {@link AuditField} creates on `batch`.
     * Call **after** enqueueing the business mutation (order matters for sequential fallback).
     */
    async enqueue(batch) {
        if (this.pending.length === 0)
            return;
        const repos = await this.resolveRepos();
        if (!repos)
            return;
        const { opRepo, fieldRepo } = repos;
        const now = new Date();
        // Placeholder only — server overwrites userId/at (§6). Required fields need a value.
        const userId = auditContext.getUserId() ?? 'unknown';
        const source = this.ctx.source ?? 'user';
        const reason = this.ctx.reason ?? null;
        for (const pending of this.pending) {
            const aggregateLabel = auditAggregateLabel(pending.aggregate.entity, pending.aggregate.record);
            // Create: names-only fields are not worth a row (no before, no value stored).
            // Update keeps them — that is what names-only is for.
            const fieldChanges = pending.op === 'create'
                ? pending.changes.filter((c) => c.level !== 'names-only')
                : pending.changes;
            if (pending.op !== 'delete' && fieldChanges.length === 0)
                continue;
            const changedCount = pending.op === 'delete'
                ? Object.keys(pending.snapshot ?? {}).length
                : fieldChanges.length;
            const operationId = batch.create(opRepo, {
                groupId: this.groupId,
                entity: pending.entity,
                recordId: pending.recordId,
                aggregateEntity: pending.aggregate.entity,
                aggregateId: pending.aggregate.id,
                aggregateLabel,
                aggregateVersion: pending.aggregate.version,
                op: pending.op,
                source,
                reason,
                userId,
                at: now,
                changedCount,
                snapshotJson: pending.snapshot != null ? toValueJson(pending.snapshot) : null,
            });
            for (const change of fieldChanges) {
                batch.create(fieldRepo, {
                    operationId,
                    groupId: this.groupId,
                    entity: pending.entity,
                    recordId: pending.recordId,
                    aggregateEntity: pending.aggregate.entity,
                    aggregateId: pending.aggregate.id,
                    aggregateLabel,
                    fieldName: change.fieldName,
                    fieldLabel: change.fieldLabel,
                    level: change.level,
                    oldValueJson: change.level === 'names-only' || pending.op === 'create'
                        ? null
                        : toValueJson(change.oldValue),
                    newValueJson: change.level === 'names-only' ? null : toValueJson(change.newValue),
                    userId,
                    at: now,
                });
            }
        }
    }
    async resolveRepos() {
        if (this.resolvedRepos !== undefined)
            return this.resolvedRepos;
        try {
            const opRepo = await repositoryResolver.resolveWithContext(AUDIT_OPERATION, { orgId: this.orgId });
            const fieldRepo = await repositoryResolver.resolveWithContext(AUDIT_FIELD, { orgId: this.orgId });
            this.resolvedRepos = { opRepo, fieldRepo };
            return this.resolvedRepos;
        }
        catch (error) {
            this.resolvedRepos = null;
            if (!unavailableOrgs.has(this.orgId)) {
                unavailableOrgs.add(this.orgId);
                const message = `[field-audit] audit repositories unavailable for org "${this.orgId}" — ` +
                    `save continues without a field-audit log`;
                rtLogService.error(message, { orgId: this.orgId, error });
                rtLogService.dispatchMonitorEvent({
                    source: MonitorEventSource.DATA_ACCESS,
                    severity: MonitorEventSeverity.ERROR,
                    type: 'field-audit-unavailable',
                    payload: { orgId: this.orgId },
                });
            }
            return null;
        }
    }
}
/** Test helper — clears the once-per-org unavailable dedup. */
export function resetAuditRecorderState() {
    unavailableOrgs.clear();
}
//# sourceMappingURL=AuditRecorder.js.map